import React, { useEffect, useRef } from 'react';
import { MessageCircle, Share2, Layers, Timer } from 'lucide-react';

const problemCards = [
  {
    icon: <MessageCircle size={28} />,
    title: "Low-Intent Leads",
    description: "Portal inquiries are information seekers, not deal-makers."
  },
  {
    icon: <Share2 size={28} />,
    title: "Lead Over-Sharing",
    description: "The same buyer details are sold to multiple agents—trust evaporates."
  },
  {
    icon: <Layers size={28} />,
    title: "Tool Overload",
    description: "Ads, socials, SEO… switching tools kills your day (and deals)."
  },
  {
    icon: <Timer size={28} />,
    title: "No Nurturing",
    description: "80% of leads go cold within 24h when follow-up isn't instant."
  }
];

const Problem = () => {
  const cardsRef = useRef([]);

  // Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add stagger delay based on index
            const index = cardsRef.current.findIndex(ref => ref === entry.target);
            setTimeout(() => {
              entry.target.classList.add('opacity-100', 'translate-y-0');
            }, index * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="problem" className="py-20 lg:py-28 bg-gray-50 dark:bg-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Why Traditional Real‑Estate Marketing{' '}
            <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
              Holds Agents Back
            </span>{' '}
            from Sustainable Growth
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-red-500 to-orange-500 mx-auto rounded-full"></div>
        </div>
        
        {/* Pain point cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {problemCards.map((card, index) => (
            <div 
              key={index} 
              className="bg-white dark:bg-slate-700 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 flex flex-col items-start opacity-0 translate-y-8 hover:-translate-y-2 border border-gray-100 dark:border-slate-600"
              ref={(el) => (cardsRef.current[index] = el)}
            >
              {/* Icon container */}
              <div className="p-4 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl text-white mb-6 shadow-lg">
                {card.icon}
              </div>
              
              {/* Content */}
              <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white leading-tight">
                {card.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {card.description}
              </p>
              
              {/* Bottom accent */}
              <div className="mt-6 w-full h-1 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-full"></div>
            </div>
          ))}
        </div>
        
        {/* Bottom CTA section */}
        <div className="text-center mt-16 lg:mt-20">
          <div className="bg-white dark:bg-slate-700 rounded-2xl p-8 lg:p-12 shadow-xl border border-gray-100 dark:border-slate-600">
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Break Free from These Limitations?
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              Our all-in-one platform eliminates these pain points and puts you in control of your lead generation.
            </p>
            <a 
              href="#solution" 
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              See Our Solution
              <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Problem;